import { useQuery, useReactiveVar } from '@apollo/client';

import { GET_USER } from './user-data.graphql';
import {
  ACCOUNT_STATUS_INACTIVE,
  ACCOUNT_STATUS_DELETED,
  ACCOUNT_SCHOOL,
} from 'global/constants';
import { InquisitiveAccountStatus } from 'global/graphql/scalars.type';
import type { GetUserQuery } from './user-data.graphql.generated';
import { signInStateVar } from './user-data.apollo';

export function isAccountInactive(status: InquisitiveAccountStatus | null) {
  return (
    status === ACCOUNT_STATUS_INACTIVE || status === ACCOUNT_STATUS_DELETED
  );
}

const useUserData = () => {
  const { isSignedIn, isStudentSignedIn, isAlreadySignedInOnce } =
    useReactiveVar(signInStateVar);

  const { data: userData } = useQuery<GetUserQuery>(GET_USER, {
    skip: !isSignedIn,
    fetchPolicy: 'cache-only',
  });
  const user = userData && userData.user;
  const canEditSelfContentOnly = !!(
    user && user.permission.canManageSelfContentOnly
  );
  const isSchoolAdmin = !!(user && user.isSchoolAdmin);
  const isSchoolTeacher = !!(user && user.isSchoolTeacher);
  const isAccountExpired = !!(user && isAccountInactive(user.accountStatus));
  const isTeacherOrAdmin = isSchoolAdmin || isSchoolTeacher;
  const isAdminOnly = !isSchoolTeacher && isSchoolAdmin;
  const canDownloadSheets = isSignedIn && isTeacherOrAdmin;
  const canViewPaidContent = isSignedIn && isSchoolTeacher;
  const canBookmarkContent = isSignedIn && isSchoolTeacher;
  const canReferForCredit = user?.permission.showAccountReferralTab;
  const canEditContent = isSignedIn && !!user?.permission.canEditContent;
  const canEditActivities = isSignedIn && !!user?.permission.canEditActivities;
  const canPublishActivities =
    isSignedIn && !!user?.permission.canPublishActivities;
  const canUpdatePassword = isSignedIn && user?.permission.canUpdatePassword;
  const canBuildActivities =
    isSignedIn && !!user?.permission.canBuildActivities;

  return {
    user: user,
    isSignedIn,
    isStudentSignedIn,
    isAlreadySignedInOnce,
    isSchoolAdmin,
    isSchoolTeacher,
    isTeacherOrAdmin,
    isAdminOnly,
    isAccountExpired,
    isInTrial: !!user?.isInTrial,
    isSchoolAccount: user?.accountType === ACCOUNT_SCHOOL,
    canEditSelfContentOnly,
    canDownloadSheets,
    canViewPaidContent,
    canBookmarkContent,
    canReferForCredit,
    canEditContent,
    canEditActivities,
    canPublishActivities,
    canBuildActivities,
    canUpdatePassword,
  };
};

export default useUserData;
